'use strict';

(() => {
  const SELECTOR = '.product-card-gallery__signature-preview[data-render-signature-preview="true"]';
  const cache = new Map();
  const CARD_W = 856;
  const CARD_H = 540;
  const MAX_DEVICE_PIXEL_RATIO = 2;
  const MOTIF_HEIGHT_RATIO = 0.4;
  const MOTIF_CENTER_Y_RATIO = 0.47;
  const MOTIF_LAYOUT_OVERRIDES = {
    always_thank_god: {
      heightRatio: 0.2,
      centerYRatio: 0.5,
      centerXRatio: 0.68,
      maxWidthRatio: 0.56,
    },
  };

  function rand(v) {
    const s = Math.sin(v * 12.9898) * 43758.5453;
    return s - Math.floor(s);
  }

  function toRgba(color, alpha) {
    const m = String(color).match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,[\d.]+)?\s*\)/i);
    if (!m) return `rgba(200,200,200,${alpha})`;
    return `rgba(${m[1]},${m[2]},${m[3]},${alpha})`;
  }

  function paintBrushedMetal(ctx, _textureImg, x, y, w, h, options = {}) {
    const seed          = options.seed          || 0;
    const sheen         = options.sheen         || 0.2;
    const streak        = options.streak        || 0.1;
    const contrast      = options.contrast      || 1;
    const tintColor     = options.tintColor     || 'rgb(208,208,208)';
    const tintAlpha     = options.tintAlpha     ?? 0.14;
    const darkenAlpha   = options.darkenAlpha   ?? 0.1;
    const brightenAlpha = options.brightenAlpha ?? 0.06;
    const baseTop       = options.baseTop       || '#d5d5d5';
    const baseMid       = options.baseMid       || '#eeeeee';
    const baseBottom    = options.baseBottom    || '#cbcbcb';

    const grad = ctx.createLinearGradient(x, y, x, y + h);
    grad.addColorStop(0, baseTop);
    grad.addColorStop(0.5, baseMid);
    grad.addColorStop(1, baseBottom);
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, w, h);

    for (let iy = 0; iy < h; iy += 2) {
      const r = rand(seed + iy * 13.17);
      const a = (0.02 + r * streak) * contrast;
      ctx.fillStyle = `rgba(255,255,255,${a.toFixed(4)})`;
      ctx.fillRect(x, y + iy, w, 1);
    }

    const shadowGrad = ctx.createLinearGradient(x, y, x, y + h);
    shadowGrad.addColorStop(0, 'rgba(0,0,0,0.14)');
    shadowGrad.addColorStop(0.12, 'rgba(0,0,0,0)');
    shadowGrad.addColorStop(0.88, 'rgba(0,0,0,0)');
    shadowGrad.addColorStop(1, 'rgba(0,0,0,0.16)');
    ctx.fillStyle = shadowGrad;
    ctx.fillRect(x, y, w, h);

    ctx.fillStyle = toRgba(tintColor, tintAlpha);
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = `rgba(0,0,0,${darkenAlpha})`;
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = `rgba(255,255,255,${brightenAlpha})`;
    ctx.fillRect(x, y, w, h);

    const sheenGrad = ctx.createLinearGradient(x, y, x + w, y + h * 0.2);
    sheenGrad.addColorStop(0, 'rgba(255,255,255,0)');
    sheenGrad.addColorStop(0.42, `rgba(255,255,255,${(sheen * 0.6).toFixed(3)})`);
    sheenGrad.addColorStop(0.5,  `rgba(255,255,255,${sheen.toFixed(3)})`);
    sheenGrad.addColorStop(0.58, `rgba(255,255,255,${(sheen * 0.6).toFixed(3)})`);
    sheenGrad.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = sheenGrad;
    ctx.fillRect(x, y, w, h);
  }

  function getEngravingStyle(finishKey) {
    const key = finishKey || '';
    if (/silver/.test(key)) {
      return { tintColor: 'rgb(0,0,0)', tintAlpha: 0.80, darkenAlpha: 0.40, brightenAlpha: 0 };
    }
    if (key === 'black_matt_gold') {
      return { tintColor: 'rgb(215,168,40)', tintAlpha: 0.82, darkenAlpha: 0.02, brightenAlpha: 0.24 };
    }
    if (/gold|rose|pink/.test(key)) {
      return { tintColor: 'rgb(210,210,210)', tintAlpha: 0.20, darkenAlpha: 0.24, brightenAlpha: 0.16 };
    }
    return { tintColor: 'rgb(175,182,195)', tintAlpha: 0.52, darkenAlpha: 0.08, brightenAlpha: 0.10 };
  }

  function parseDatasetNumber(value) {
    if (value == null || value === '') return null;
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function getMotifLayout(node) {
    const base = {
      heightRatio: MOTIF_HEIGHT_RATIO,
      centerYRatio: MOTIF_CENTER_Y_RATIO,
      centerXRatio: 0.5,
      maxWidthRatio: null,
    };
    const motifFilename = String(node.dataset.motifFilename || '').toLowerCase();
    let layout = base;
    if (motifFilename) {
      for (const [key, override] of Object.entries(MOTIF_LAYOUT_OVERRIDES)) {
        if (motifFilename.includes(key)) {
          layout = {
            heightRatio: override.heightRatio ?? base.heightRatio,
            centerYRatio: override.centerYRatio ?? base.centerYRatio,
            centerXRatio: override.centerXRatio ?? base.centerXRatio,
            maxWidthRatio: override.maxWidthRatio ?? base.maxWidthRatio,
          };
          break;
        }
      }
    }

    const motifOffsetX = parseDatasetNumber(node.dataset.motifOffsetX);
    const motifOffsetY = parseDatasetNumber(node.dataset.motifOffsetY);
    const motifScale = parseDatasetNumber(node.dataset.motifScale);
    const motifScaleX = parseDatasetNumber(node.dataset.motifScaleX);

    if (motifOffsetX != null) layout.centerXRatio = Math.max(0.08, Math.min(0.92, motifOffsetX));
    if (motifOffsetY != null) layout.centerYRatio = Math.max(0.12, Math.min(0.88, motifOffsetY));
    if (motifScale != null) layout.heightRatio = Math.max(0.08, Math.min(0.6, motifScale));
    layout.scaleX = motifScaleX ?? null;
    return layout;
  }

  function loadImage(url) {
    if (!url) return Promise.resolve(null);
    if (cache.has(url)) return Promise.resolve(cache.get(url));
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        cache.set(url, img);
        resolve(img);
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });
  }

  function getCardClipRect(img, W, H) {
    if (!img) return { x: 0, y: 0, w: W, h: H };
    try {
      const off = document.createElement('canvas');
      off.width = W; off.height = H;
      const oc = off.getContext('2d');
      if (!oc) return { x: 0, y: 0, w: W, h: H };
      oc.drawImage(img, 0, 0, W, H);
      const data = oc.getImageData(0, 0, W, H).data;
      let minX = W, minY = H, maxX = -1, maxY = -1;
      for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
          if (data[((y * W) + x) * 4 + 3] <= 8) continue;
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
      if (maxX < minX || maxY < minY) return { x: 0, y: 0, w: W, h: H };
      const rect = { x: Math.max(0, minX), y: Math.max(0, minY), w: maxX - minX + 1, h: maxY - minY + 1 };
      if (rect.w < W * 0.5 || rect.h < H * 0.5) return { x: 0, y: 0, w: W, h: H };
      return rect;
    } catch (e) {
      return { x: 0, y: 0, w: W, h: H };
    }
  }

  function roundRectPath(ctx, x, y, w, h, r) {
    const radius = Math.max(0, Math.min(r, Math.floor(Math.min(w, h) / 2)));
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + w - radius, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
    ctx.lineTo(x + w, y + h - radius);
    ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
    ctx.lineTo(x + radius, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }

  function drawTiledTexture(ctx, textureImg, x, y, w, h, scale) {
    const tw = Math.max(24, Math.round(textureImg.width * scale));
    const th = Math.max(24, Math.round(textureImg.height * scale));
    for (let yy = y; yy < y + h; yy += th) {
      const dh = Math.min(th, y + h - yy);
      for (let xx = x; xx < x + w; xx += tw) {
        const dw = Math.min(tw, x + w - xx);
        ctx.drawImage(textureImg, 0, 0, textureImg.width, textureImg.height, xx, yy, dw, dh);
      }
    }
  }

  function prepareHiDPICanvas(canvas, previewNode) {
    const cssW = Math.max(1, Math.round(previewNode.clientWidth || canvas.clientWidth || CARD_W));
    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO);
    const scale = Math.max(1, (cssW / CARD_W) * dpr);
    const pixelW = Math.max(1, Math.round(CARD_W * scale));
    const pixelH = Math.max(1, Math.round(CARD_H * scale));
    if (canvas.width !== pixelW || canvas.height !== pixelH) {
      canvas.width = pixelW;
      canvas.height = pixelH;
    }
    return { scale, pixelW, pixelH };
  }

  function makeLumaMask(maskCtx, motifImg, x, y, w, h) {
    maskCtx.clearRect(0, 0, maskCtx.canvas.width, maskCtx.canvas.height);
    maskCtx.drawImage(motifImg, x, y, w, h);
    const data = maskCtx.getImageData(x, y, w, h);
    const px = data.data;
    for (let i = 0; i < px.length; i += 4) {
      const a = px[i + 3] / 255;
      if (a <= 0) {
        px[i + 3] = 0;
        continue;
      }
      const lum = (px[i] + px[i + 1] + px[i + 2]) / 3;
      const strength = 1 - (lum / 255);
      const alpha = Math.max(0, Math.min(1, Math.pow(strength, 0.85) * a));
      px[i] = 255;
      px[i + 1] = 255;
      px[i + 2] = 255;
      px[i + 3] = Math.round(alpha * 255);
    }
    maskCtx.putImageData(data, x, y);
  }

  function renderPreview(node, blankImg, motifImg, textureImg) {
    const canvas = node.querySelector('.product-card-gallery__signature-canvas');
    if (!canvas || !blankImg || !motifImg) return false;
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;

    const W = CARD_W;
    const H = CARD_H;
    const { scale, pixelW, pixelH } = prepareHiDPICanvas(canvas, node);
    const px = (n) => Math.max(1, Math.round(n * scale));
    const pixelOffset = Math.max(1, Math.round(scale));

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(blankImg, 0, 0, W, H);

    const motifLayout = getMotifLayout(node);
    const motifAspect = motifImg.width / Math.max(1, motifImg.height);
    const isWideMotif = motifAspect >= 1.9;
    const resolvedHeightRatio = isWideMotif
      ? Math.min(motifLayout.heightRatio, 0.26)
      : motifLayout.heightRatio;
    const resolvedMaxWidthRatio = motifLayout.maxWidthRatio || (isWideMotif ? 0.74 : null);
    let motifH = Math.max(1, Math.round(H * resolvedHeightRatio));
    let motifW = Math.max(1, Math.round(motifImg.width * (motifH / Math.max(1, motifImg.height))));
    if (resolvedMaxWidthRatio) {
      const maxMotifW = Math.max(1, Math.round(W * resolvedMaxWidthRatio));
      if (motifW > maxMotifW) {
        const s = maxMotifW / motifW;
        motifW = maxMotifW;
        motifH = Math.max(1, Math.round(motifH * s));
      }
    }
    if (motifLayout.scaleX != null) motifW = Math.max(1, Math.round(motifW * motifLayout.scaleX));
    const motifX = Math.round((W * (motifLayout.centerXRatio ?? 0.5)) - (motifW / 2));
    const motifY = Math.round((H * (motifLayout.centerYRatio ?? MOTIF_CENTER_Y_RATIO)) - (motifH / 2));
    const motifPxX = px(motifX);
    const motifPxY = px(motifY);
    const motifPxW = px(motifW);
    const motifPxH = px(motifH);

    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = pixelW;
    maskCanvas.height = pixelH;
    const mc = maskCanvas.getContext('2d');
    if (!mc) return;
    makeLumaMask(mc, motifImg, motifPxX, motifPxY, motifPxW, motifPxH);

    const finishKey = String(node.dataset.finishKey || '');
    const isGoldOnBlack = finishKey === 'black_matt_gold';
    const isSilver = /silver/.test(finishKey);
    const engStyle = getEngravingStyle(finishKey);

    const engraveCanvas = document.createElement('canvas');
    engraveCanvas.width = pixelW;
    engraveCanvas.height = pixelH;
    const ec = engraveCanvas.getContext('2d');
    if (!ec) return;

    paintBrushedMetal(ec, null, motifPxX, motifPxY, motifPxW, motifPxH, {
      seed: 11.3,
      sheen:         isGoldOnBlack ? 0.18 : (isSilver ? 0.36 : 0.42),
      streak:        isGoldOnBlack ? 0.16 : (isSilver ? 0.16 : 0.18),
      contrast:      isGoldOnBlack ? 1.16 : (isSilver ? 1.16 : 1.2),
      baseTop: '#e2e2e2', baseMid: '#ffffff', baseBottom: '#c4c4c4',
      tintColor:     engStyle.tintColor,
      tintAlpha:     engStyle.tintAlpha,
      darkenAlpha:   engStyle.darkenAlpha,
      brightenAlpha: engStyle.brightenAlpha,
    });

    ec.globalCompositeOperation = 'destination-in';
    ec.drawImage(maskCanvas, 0, 0);
    ec.globalCompositeOperation = 'source-over';

    const hlCanvas = document.createElement('canvas');
    hlCanvas.width = pixelW;
    hlCanvas.height = pixelH;
    const hc = hlCanvas.getContext('2d');
    if (hc) {
      hc.drawImage(maskCanvas, 0, 0);
      hc.globalCompositeOperation = 'source-in';
      const hlGrad = hc.createLinearGradient(motifPxX, motifPxY, motifPxX + motifPxW * 0.3, motifPxY + motifPxH * 0.3);
      hlGrad.addColorStop(0, 'rgba(255,255,255,0.55)');
      hlGrad.addColorStop(1, 'rgba(255,255,255,0)');
      hc.fillStyle = hlGrad;
      hc.fillRect(0, 0, pixelW, pixelH);
      hc.globalCompositeOperation = 'source-over';
    }

    const shCanvas = document.createElement('canvas');
    shCanvas.width = pixelW;
    shCanvas.height = pixelH;
    const sc = shCanvas.getContext('2d');
    if (sc) {
      sc.drawImage(maskCanvas, 0, 0);
      sc.globalCompositeOperation = 'source-in';
      const shGrad = sc.createLinearGradient(motifPxX + motifPxW, motifPxY + motifPxH, motifPxX + motifPxW * 0.7, motifPxY + motifPxH * 0.7);
      shGrad.addColorStop(0, 'rgba(0,0,0,0.38)');
      shGrad.addColorStop(1, 'rgba(0,0,0,0)');
      sc.fillStyle = shGrad;
      sc.fillRect(0, 0, pixelW, pixelH);
      sc.globalCompositeOperation = 'source-over';
    }

    const cardClipRect = getCardClipRect(blankImg, W, H);
    const cardClipRadius = Math.round(cardClipRect.h * 0.06);
    ctx.save();
    roundRectPath(ctx, cardClipRect.x, cardClipRect.y, cardClipRect.w, cardClipRect.h, cardClipRadius);
    ctx.clip();
    ctx.shadowColor = isGoldOnBlack ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.22)';
    ctx.shadowBlur  = isGoldOnBlack ? 5 : 8;
    ctx.drawImage(engraveCanvas, 0, 0, W, H);
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur  = 0;
    ctx.drawImage(hlCanvas, 0, 0, W, H);
    ctx.drawImage(shCanvas, 0, 0, W, H);
    ctx.restore();
    return true;
  }

  async function renderNode(node) {
    if (node.dataset.previewReady === 'true') return;
    const blankUrl = node.dataset.blankUrl || '';
    const motifUrl = node.dataset.motifUrl || '';
    const textureUrl = node.dataset.textureUrl || '';

    const [blankImg, motifImg, textureImg] = await Promise.all([
      loadImage(blankUrl),
      loadImage(motifUrl),
      loadImage(textureUrl),
    ]);
    const rendered = renderPreview(node, blankImg, motifImg, textureImg);
    if (rendered) {
      node.classList.add('is-rendered');
      node.dataset.previewReady = 'true';
    }
  }

  function init() {
    const nodes = document.querySelectorAll(SELECTOR);
    if (!nodes.length) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              observer.unobserve(entry.target);
              renderNode(entry.target).catch(() => {});
            }
          });
        },
        { rootMargin: '300px' }
      );
      nodes.forEach((node) => observer.observe(node));
    } else {
      nodes.forEach((node) => renderNode(node).catch(() => {}));
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
